import { useEffect, useState } from "react";
import { AlertCircle, ArrowUpRight, ArrowDownRight, Clock, Radio, Zap, Moon, Ban } from "lucide-react";
import { api } from "@/lib/api";
import { DescriptiveChip } from "@/components/Validation";

const SIG_STYLE = {
  LONG: { border: "border-emerald-500/60", bg: "bg-emerald-500/10", text: "text-emerald-300", icon: ArrowUpRight },
  SHORT: { border: "border-red-500/60", bg: "bg-red-500/10", text: "text-red-300", icon: ArrowDownRight },
  WAIT: { border: "border-slate-600", bg: "bg-slate-800/40", text: "text-slate-300", icon: Clock },
};

const STATUS_META = {
  MARKET_CLOSED: { label: "Market Closed", icon: Moon, cls: "border-slate-600 bg-slate-800/50 text-slate-300" },
  NO_ENTRY_WINDOW: { label: "Entries Closed (<75 min to close)", icon: Ban, cls: "border-amber-500/50 bg-amber-500/10 text-amber-300" },
  DATA_QUALITY_WARNING: { label: "Data Quality Warning", icon: AlertCircle, cls: "border-amber-500/50 bg-amber-500/10 text-amber-300" },
};

function fmt(v, digits = 2) {
  if (v == null) return "—";
  return Number(v).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function useCountdown(targetMs) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!targetMs) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetMs]);
  if (!targetMs) return null;
  const secs = Math.max(0, Math.floor((targetMs - now) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
}

export default function IntradaySignalCard({ result }) {
  const timeExit = result?.entry_plan?.time_exit_ts_ms;
  const countdown = useCountdown(timeExit);
  const [similarity, setSimilarity] = useState(null);
  useEffect(() => {
    if (!result || !["LONG", "SHORT"].includes(result.signal)) { setSimilarity(null); return; }
    let cancelled = false;
    api.post("/intraday/similarity/analyze", result)
      .then((r) => { if (!cancelled) setSimilarity(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [result?.ticker, result?.signal, result?.decision_bar_ts]);
  if (!result) return null;
  const status = result.status;
  if (status && status !== "OK") {
    const meta = STATUS_META[status] || STATUS_META.DATA_QUALITY_WARNING;
    const Icon = meta.icon;
    return (
      <div
        data-testid="intraday-signal-card"
        data-status={status}
        className={`border p-4 flex items-start gap-3 ${meta.cls}`}
      >
        <Icon className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="mono text-xs uppercase tracking-widest font-bold flex items-center gap-2">
            {meta.label}
            <DescriptiveChip testid="intraday-descriptive-chip" />
          </div>
          {result.why_wait && (
            <div className="mono text-[11px] text-slate-400 mt-1" data-testid="intraday-why-wait">
              {result.why_wait}
            </div>
          )}
        </div>
      </div>
    );
  }

  const sig = result.signal || "WAIT";
  const st = SIG_STYLE[sig] || SIG_STYLE.WAIT;
  const Icon = st.icon;
  const isDir = sig === "LONG" || sig === "SHORT";
  const plan = result.entry_plan || {};
  const score = sig === "LONG" ? result.long_score : sig === "SHORT" ? result.short_score : Math.max(result.long_score || 0, result.short_score || 0);

  return (
    <div
      data-testid="intraday-signal-card"
      data-signal={sig}
      className={`border ${st.border} ${st.bg}`}
    >
      <div className="flex items-center gap-3 border-b border-[#1E293B] px-4 py-3">
        <div className={`h-10 w-10 flex items-center justify-center border ${st.border} ${st.bg}`}>
          <Icon className={`h-5 w-5 ${st.text}`} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`mono text-2xl font-extrabold tracking-tight ${st.text}`} data-testid="intraday-signal-label">
              {sig}
            </span>
            <span className="mono text-[10px] px-1.5 py-0.5 border border-amber-500/40 text-amber-300 uppercase tracking-widest flex items-center gap-1">
              <Zap className="h-2.5 w-2.5" /> Intraday
            </span>
            {result.strength_band && (
              <span className="mono text-[10px] px-1.5 py-0.5 border border-slate-700 text-slate-400 uppercase tracking-widest">
                {result.strength_band}
              </span>
            )}
            {/* I9 verdict: NULL predictive edge on intraday. Confidence pill is
                intentionally replaced with a "Descriptive" chip so users know
                confidence isn't hidden — it's honestly relabeled. */}
            <DescriptiveChip testid="intraday-descriptive-chip" />
          </div>
          <div className="mono text-[11px] text-slate-500 uppercase tracking-widest mt-0.5">
            Score {fmt(score, 1)} · L {fmt(result.long_score, 1)} · S {fmt(result.short_score, 1)}
          </div>
        </div>
        {isDir && (
          <div className="flex flex-col items-end">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Time-Exit In</div>
            <div className="mono text-lg font-bold tabular-nums text-slate-100" data-testid="intraday-time-exit">
              {countdown || "—"}
            </div>
          </div>
        )}
      </div>

      {isDir && (
        <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Ideal Entry" value={fmt(plan.ideal_entry)} />
          <Stat label="Zone" value={plan.entry_zone ? `${fmt(plan.entry_zone.low)} – ${fmt(plan.entry_zone.high)}` : "—"} />
          <Stat label="Stop" value={fmt(plan.stop_loss)} tone="red" />
          <Stat label="Do Not Chase" value={fmt(plan.do_not_chase)} tone="muted" />
          <Stat label="T1" value={fmt(plan.targets?.t1)} tone={sig === "LONG" ? "green" : "red"} />
          <Stat label="T2" value={fmt(plan.targets?.t2)} tone={sig === "LONG" ? "green" : "red"} />
          <Stat label="R/R (T1)" value={plan.risk_reward_t1 != null ? `${fmt(plan.risk_reward_t1, 2)}R` : "—"} />
          <Stat label="Feed" value={(result.feed || "sip").toUpperCase()} tone="muted" />
        </div>
      )}
      {isDir && plan.invalidation && (
        <div className="border-t border-[#1E293B] px-4 py-2 mono text-[11px] text-slate-400">
          <span className="text-slate-500 uppercase tracking-widest text-[10px] mr-2">Invalidation:</span>
          {plan.invalidation.human}
        </div>
      )}
      {isDir && similarity && (
        <div data-testid="intraday-similarity" className="border-t border-[#1E293B] px-4 py-2 mono text-[11px] text-slate-400 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="text-slate-500 uppercase tracking-widest text-[10px]" title="Descriptive statistics from a population with no validated predictive edge (I9).">Descriptive stats:</span>
          {similarity.eligible ? (
            <>
              <span>
                Similar setups <span className="text-slate-200 font-bold">{similarity.sample_size}</span>
              </span>
              <span>
                Win rate <span className="text-slate-200 font-bold">{similarity.win_rate_pct?.toFixed?.(1)}%</span>
              </span>
              {similarity.median_r != null && (
                <span>
                  Median <span className="text-slate-200 font-bold">{similarity.median_r.toFixed(2)}R</span>
                </span>
              )}
              <span className="text-slate-500">avg sim {similarity.avg_similarity}</span>
              <span className="text-slate-500 italic">no validated edge — context only</span>
            </>
          ) : (
            <span data-testid="intraday-similarity-insufficient" className="text-slate-500">
              Insufficient similar setups ({similarity.sample_size ?? 0}/{similarity.min_sample_size ?? 20}).
            </span>
          )}
        </div>
      )}
      {!isDir && result.why_wait && (
        <div className="px-4 py-3 mono text-[11px] text-slate-400" data-testid="intraday-why-wait">
          <span className="text-slate-500 uppercase tracking-widest text-[10px] mr-2">Why wait:</span>
          {result.why_wait}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls =
    tone === "green" ? "text-emerald-300"
    : tone === "red" ? "text-red-300"
    : tone === "muted" ? "text-slate-500"
    : "text-slate-100";
  return (
    <div className="flex flex-col">
      <span className="mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <span className={`mono text-sm tabular-nums font-bold ${cls}`}>{value}</span>
    </div>
  );
}
